import React from "react";
import { Link } from "react-router-dom";

const LearnHowPage: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold" style={{ color: "#0A2A4A" }}>
            Pureaum
          </Link>
          <Link to="/" className="text-sm font-semibold hover:underline" style={{ color: "#0A2A4A" }}>
            Back to homepage
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-10">
        <section className="space-y-4">
          <h1 className="text-3xl font-bold" style={{ color: "#0A2A4A" }}>
            Learn How Pureaum Stops Counterfeits at Scale
          </h1>
          <p className="text-sm max-w-3xl" style={{ color: "#2B2B2B" }}>
            Counterfeiting isn’t just fake labels—it’s behavior in the market. Pureaum detects reuse, anomalies, and suspicious distribution patterns using Cloud Scan Intelligence.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
            Why copied labels break most anti-counterfeit systems
          </h2>
          <ul className="list-disc list-inside space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
            <li>Static identifiers can be duplicated (QR, barcode, serial printed on pack).</li>
            <li>Copying looks “valid” unless the system understands expected lifecycle behavior.</li>
            <li>Fraud shows patterns: duplicate scans, impossible travel, scan clustering, post-consumption scans.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
            How Pureaum works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { title: "Assign Secure Identity", body: "Each item/batch gets a unique identity." },
              { title: "Scan & Verify", body: "Consumers/partners scan to verify authenticity." },
              { title: "Capture Signals", body: "Time, location, scan frequency, retailer/region patterns (privacy-safe)." },
              { title: "Detect Anomalies", body: "Duplicate scans, geo-velocity anomalies, suspicious clusters, post-consumption scanning." },
              { title: "Act", body: "Alerts + evidence for brand action (investigation, takedown, targeted interventions)." },
            ].map((step, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm h-full">
                <div className="text-xs font-bold mb-2" style={{ color: "#0A2A4A" }}>
                  Step {idx + 1}
                </div>
                <h3 className="font-semibold mb-2" style={{ color: "#0A2A4A" }}>
                  {step.title}
                </h3>
                <p className="text-sm" style={{ color: "#2B2B2B" }}>
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-xl font-bold mb-2" style={{ color: "#0A2A4A" }}>
              Cloud Scan Intelligence (core differentiation)
            </h2>
            <p className="text-sm mb-3" style={{ color: "#2B2B2B" }}>
              Always-on monitoring that learns from real-world scans, flags correlated counterfeit signals, and generates actionable evidence.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Duplicate scans across locations",
                "Geo-velocity anomalies",
                "Retailer/market clustering",
                "Post-consumption scan activity",
              ].map((signal) => (
                <div key={signal} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm text-sm" style={{ color: "#2B2B2B" }}>
                  {signal}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
            Use cases
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Food staples & dairy", body: "High velocity, label reuse risk." },
              { title: "OTC health products", body: "Safety and compliance sensitive." },
              { title: "Premium consumer goods", body: "Protect brand trust at shelf." },
              { title: "Electronics/accessories", body: "Channel control and leakage detection." },
            ].map((u) => (
              <div key={u.title} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <h3 className="font-semibold mb-2" style={{ color: "#0A2A4A" }}>
                  {u.title}
                </h3>
                <p className="text-sm" style={{ color: "#2B2B2B" }}>
                  {u.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
            Deployment options
          </h2>
          <ul className="list-disc list-inside space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
            <li>QR only (outer read-only + optional inner consume/secure)</li>
            <li>QR + packaging hierarchy (case/pallet/unit)</li>
            <li>API-first integration (existing apps / retailer systems)</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
            FAQs
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { q: "What happens if customers don’t mark consumed?", a: "Cloud signals still track risk; Inner QR can add consume proof for higher confidence." },
              { q: "Does this require personal data?", a: "No personal data is required; signals are privacy-safe." },
              { q: "Can counterfeiters copy the QR?", a: "They can copy codes, but Cloud Scan Intelligence detects abnormal reuse patterns." },
              { q: "What is the minimum viable pilot?", a: "Typically a few SKUs and regions over 4–8 weeks to gather signals." },
              { q: "How do alerts work?", a: "Alerts include evidence (locations, timelines, affected IDs/batches) for action." },
              { q: "How long does onboarding take?", a: "Most teams start scanning within weeks with light integration." },
            ].map((item) => (
              <div key={item.q} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <h3 className="font-semibold mb-2" style={{ color: "#0A2A4A" }}>
                  {item.q}
                </h3>
                <p className="text-sm" style={{ color: "#2B2B2B" }}>
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div>
              <h2 className="text-xl font-bold mb-2" style={{ color: "#0A2A4A" }}>
                Ready to move forward?
              </h2>
              <p className="text-sm mb-3" style={{ color: "#2B2B2B" }}>
                Choose a path: start a pilot or ask us a question.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact-sales"
                className="px-6 py-3 rounded-lg font-semibold text-center"
                style={{ backgroundColor: "#FF7A29", color: "white" }}
                onClick={() => console.log("cta_learn_how_start_pilot_click")}
              >
                Start a Pilot
              </Link>
              <Link
                to="/talk-to-us"
                className="px-6 py-3 rounded-lg font-semibold text-center border-2"
                style={{ color: "#0A2A4A", borderColor: "#0A2A4A" }}
                onClick={() => console.log("cta_learn_how_talk_to_us_click")}
              >
                Ask a Question
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LearnHowPage;
